import { apiClient } from '@/services/apiClient';
import { apiConfig } from '@/config/apiConfig';
import { handleServiceError } from '@/utils/errorHandler';
import type { CaseListParams, CaseListResponse, OnboardingCase } from '../types/onboarding';
import type { WorkflowState, WorkflowSummary } from '../types/workflow';
import type {
  DocumentVersionDetails,
  ExistingSubmissionQuery,
  ExistingSubmissionResponse,
  FormSchema,
  FormSubmitValues,
  GridSubmissionRow,
  UploadedDocument,
} from '../form-builder/types';
import {
  getDocumentVersion as documentApiGetVersion,
  uploadDocument as documentApiUpload,
  type GetDocumentVersionArgs,
  type UploadDocumentArgs,
} from './documentApi';
import {
  getLocationDistricts,
  getLocationSubdistricts,
  getLocationVillages,
  getVillageByLgd as locationApiGetVillage,
} from './locationApi';
import type { Subdistrict, Village } from '@/services/locationService';

interface FormMetadataResponse {
  message: string;
  data: FormSchema;
}

interface CreateCaseResponse {
  message: string;
  id: number;
}

/**
 * Filters accepted by `GET /console/user_onboarding/list/export`. Same shape
 * as `CaseListParams` minus `page` / `page_size` — the export endpoint
 * returns all matching rows, up to the documented 10,000-row cap.
 */
export type CaseExportParams = Omit<CaseListParams, 'page' | 'page_size'>;

/**
 * Parse the standard `Content-Disposition` header for an attachment filename
 * (supports both `filename="..."` and RFC 5987 `filename*=UTF-8''...`).
 * Returns `fallback` when the header is absent / unparseable.
 */
function extractFilename(header: string | undefined, fallback: string): string {
  if (!header) return fallback;
  const m =
    /filename\*=(?:UTF-8'')?"?([^";\n]+)"?/i.exec(header) ||
    /filename="?([^";\n]+)"?/i.exec(header);
  if (!m?.[1]) return fallback;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/** Trigger a browser download for CSV bytes. */
function triggerCsvDownload(data: BlobPart, filename: string): void {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * When the request was made with `responseType: 'blob'`, axios returns the
 * error body as a Blob too — so the JSON `{message, error}` envelope isn't
 * automatically parsed. Read it as text, parse, and re-throw a normal Error
 * with the server message attached so the UI can surface the right copy
 * (e.g. `EXPORT_LIMIT_EXCEEDED`, `NO_DATA`).
 */
async function wrapExportError(error: any, fallback: string): Promise<Error> {
  const status: number | undefined = error?.response?.status;
  let message = error?.message || fallback;
  let serverError: unknown = undefined;
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      message = parsed?.message || parsed?.error || message;
      serverError = parsed;
    } catch {
      // Body wasn't JSON — keep the fallback / axios message.
    }
  } else if (data && typeof data === 'object') {
    message = (data as any).message || (data as any).error || message;
    serverError = data;
  }
  const wrapped = new Error(message);
  (wrapped as any).status = status;
  (wrapped as any).serverError = serverError;
  return wrapped;
}

/**
 * Peel the "list of rows" out of any of the envelope shapes the
 * mis-api uses across `/console/*` endpoints: bare arrays,
 * `{ data: [...] }`, `{ list: [...] }`, `{ resources: [...] }`, and
 * the current staging convention `{ success, data: { list: [...] } }`.
 *
 * Unrecognised shapes fall through to `[]` so a mis-shaped response
 * lands as an empty state in the UI rather than crashing render.
 */
function extractList<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  if (!body || typeof body !== 'object') return [];
  const b = body as Record<string, unknown>;
  if (Array.isArray(b.list)) return b.list as T[];
  if (Array.isArray(b.resources)) return b.resources as T[];
  if (Array.isArray(b.data)) return b.data as T[];
  if (b.data && typeof b.data === 'object') {
    const d = b.data as Record<string, unknown>;
    if (Array.isArray(d.list)) return d.list as T[];
    if (Array.isArray(d.data)) return d.data as T[];
  }
  return [];
}

class OnboardingService {
  private endpoints = apiConfig.endpoints.userOnboarding;

  async getCases(params: CaseListParams = {}): Promise<CaseListResponse['data']> {
    try {
      const response = await apiClient.get<CaseListResponse>(this.endpoints.list, { params });
      return response.data.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Onboarding Cases');
    }
  }

  /**
   * Export cases matching the supplied filters as a CSV file. Identical
   * filter / sort params as `getCases` minus pagination (the server returns
   * all matching rows, capped at 10,000). Triggers a browser download with
   * the filename from the server's `Content-Disposition`, falling back to
   * `onboarding_cases_<YYYY-MM-DD>.csv` when the header is absent or
   * stripped by a proxy.
   *
   * Surfaces server-side error envelopes ({message, error}) so the UI can
   * show actionable copy for the documented failure modes —
   * `EXPORT_LIMIT_EXCEEDED` (>10k rows) and `NO_DATA` (no matches).
   */
  async exportCases(params: CaseExportParams = {}): Promise<void> {
    try {
      const response = await apiClient.get(this.endpoints.listExport, {
        params,
        responseType: 'blob',
        timeout: 120000,
      });
      const filename = extractFilename(
        response.headers?.['content-disposition'],
        `onboarding_cases_${new Date().toISOString().slice(0, 10)}.csv`,
      );
      triggerCsvDownload(response.data, filename);
    } catch (error: any) {
      throw await wrapExportError(error, 'Could not export cases. Please try again.');
    }
  }

  async getCaseById(id: number): Promise<OnboardingCase> {
    try {
      const response = await apiClient.get<{ message: string; data: OnboardingCase }>(
        `${this.endpoints.detail}/${id}`,
      );
      return response.data.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Onboarding Case');
    }
  }

  /**
   * Registered workflows for the current user. Drives the workflow
   * picker on the cases list + the "Start" chooser. Each row carries
   * `abbreviation` (segment for `/create/:abv` and `?workflow_abv=`)
   * and permission flags (`can_list`, `can_initiate`) that the UI
   * honours instead of hardcoding role strings.
   *
   * Response envelope handled permissively — accepts bare arrays,
   * `{ data: [...] }`, and `{ success, data: { list: [...] } }`
   * (the shape used across `/console/*` list endpoints in this API).
   */
  async getWorkflows(): Promise<WorkflowSummary[]> {
    try {
      const response = await apiClient.get<unknown>(this.endpoints.workflows);
      return extractList<WorkflowSummary>(response.data);
    } catch (error: any) {
      handleServiceError(error, 'Fetch Workflows');
    }
  }

  /**
   * States for one workflow — the `title` of each entry is the value
   * sent to `?status=` on the cases list. Callers should reload
   * whenever the selected workflow changes (spec: "Do not reuse the
   * static ONB state list from the older docs for CR").
   */
  async getWorkflowStates(workflowAbv: string): Promise<WorkflowState[]> {
    try {
      const url = `${this.endpoints.workflowStatesBase}/${encodeURIComponent(workflowAbv)}/states`;
      const response = await apiClient.get<unknown>(url);
      return extractList<WorkflowState>(response.data);
    } catch (error: any) {
      handleServiceError(error, 'Fetch Workflow States');
    }
  }

  /**
   * Form metadata for the create page. Omitting `workflowAbv` hits
   * the bare `/create` endpoint, which the server defaults to `ONB`
   * — preserves behaviour for callers that haven't been updated to
   * pass an explicit workflow yet.
   */
  async getCreateFormMetadata(workflowAbv?: string): Promise<FormSchema> {
    try {
      const url = workflowAbv
        ? `${this.endpoints.create}/${encodeURIComponent(workflowAbv)}`
        : this.endpoints.create;
      const response = await apiClient.get<FormMetadataResponse>(url);
      return response.data.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Onboarding Form');
    }
  }

  /**
   * Submit a new case. Same optional-abv contract as
   * `getCreateFormMetadata` — bare `/create` still lands on ONB.
   */
  async createCase(
    payload: FormSubmitValues,
    workflowAbv?: string,
  ): Promise<CreateCaseResponse> {
    try {
      const url = workflowAbv
        ? `${this.endpoints.create}/${encodeURIComponent(workflowAbv)}`
        : this.endpoints.create;
      const response = await apiClient.post<CreateCaseResponse>(url, payload);
      return response.data;
    } catch (error: any) {
      // Surface server-provided message/error verbatim when available — the backend
      // sends `{ message, error }` on insertion validation failures (see API doc §4).
      const data = error?.response?.data;
      if (data && (data.message || data.error)) {
        const wrapped = new Error(data.message || data.error);
        (wrapped as any).serverError = data;
        throw wrapped;
      }
      handleServiceError(error, 'Create Onboarding Case');
    }
  }

  /**
   * Look up an existing submission for `form_name` keyed by identifier query
   * params (matched to `FormSchema.identifier`). Used to prefill the create
   * form when the user enters identifiers (e.g. email) that already have a
   * record on the server.
   */
  async getExistingSubmission(
    formName: string,
    query: ExistingSubmissionQuery,
  ): Promise<ExistingSubmissionResponse> {
    const url = `${this.endpoints.existingSubmission}/${encodeURIComponent(
      formName,
    )}/existing_submission`;
    const response = await apiClient.get<ExistingSubmissionResponse>(url, { params: query });
    return response.data;
  }

  /**
   * Upload a candidate document (PAN / Aadhaar / etc.) as the next version
   * of `(entity, document_type)`. Backoffice JWT path — the server records
   * `uploaded_by_source = "BACKOFFICE"`.
   */
  uploadDocument(args: UploadDocumentArgs): Promise<UploadedDocument> {
    return documentApiUpload(apiClient, args);
  }

  /** Fetch the presigned URL + metadata for one document version. */
  getDocumentVersion(args: GetDocumentVersionArgs): Promise<DocumentVersionDetails> {
    return documentApiGetVersion(apiClient, args);
  }

  /**
   * Cascading location lookup for onboarding forms — hits the same invite
   * mount that the candidate portal uses, but with the backoffice JWT.
   * Mounted under `/console/user_onboarding/invite/location/...` per
   * `docs/onboarding/API/API-doc.md` §5; both auth modes are accepted.
   */
  getOnboardingLocationDistricts(): Promise<string[]> {
    return getLocationDistricts(apiClient);
  }

  getOnboardingLocationSubdistricts(district: string): Promise<Subdistrict[]> {
    return getLocationSubdistricts(apiClient, district);
  }

  getOnboardingLocationVillages(district: string, subdistrict: string): Promise<Village[]> {
    return getLocationVillages(apiClient, district, subdistrict);
  }

  /**
   * Reverse-lookup a village by its LGD / master location id (the value
   * stored in `master_location_lookup_id` on candidate submissions).
   * Returns the human-readable names plus pincode — see
   * `docs/onboarding/API/User-Onboarding.md` §5.1.
   *
   * Mounted under the invite tree but accepts the backoffice JWT, so the
   * detail page can use it without an invite token.
   */
  async getVillageByLgd(lgdCode: string | number): Promise<VillageLookupDetails> {
    const result = await locationApiGetVillage(apiClient, lgdCode);
    return (result ?? {}) as VillageLookupDetails;
  }

  /**
   * Fetch existing per-row submission records for a `grid_input` form.
   * Path: `GET /form/:form_name/submissions/:submission_id` (spec §6.2).
   *
   * `submission_id` is the workflow entity id (e.g. onboarding case id).
   * Response shape varies — custom resources commonly return an array under
   * `data`, but a bare array body is tolerated as a fallback.
   */
  async getGridSubmissions(
    formName: string,
    submissionId: number | string,
  ): Promise<GridSubmissionRow[]> {
    const url = `/form/${encodeURIComponent(formName)}/submissions/${encodeURIComponent(
      String(submissionId),
    )}`;
    const response = await apiClient.get<{ data?: GridSubmissionRow[] } | GridSubmissionRow[]>(url);
    const body = response.data as unknown;
    if (Array.isArray(body)) return body as GridSubmissionRow[];
    if (body && typeof body === 'object') {
      const inner = (body as { data?: unknown }).data;
      if (Array.isArray(inner)) return inner as GridSubmissionRow[];
    }
    return [];
  }

  /**
   * Save one grid row. `POST /form/:form_name` with the row payload
   * (spec §6.3). Body must include the `uidt:"ID"` column value plus every
   * `default` field; server-required context such as `entity_id` is added
   * by the caller.
   */
  async saveGridRow(
    formName: string,
    body: Record<string, unknown>,
  ): Promise<{ id?: number; message?: string }> {
    const url = `/form/${encodeURIComponent(formName)}`;
    try {
      const response = await apiClient.post<{ id?: number; message?: string }>(url, body);
      return response.data;
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        (data && (data.message || data.error)) || error?.message || 'Could not save row.';
      const wrapped = new Error(message);
      (wrapped as any).status = error?.response?.status;
      (wrapped as any).serverError = data;
      throw wrapped;
    }
  }

  /**
   * Fetch the server-side prefill used to create a MIS user from an
   * onboarded candidate. Identity fields (name, email, employee_id, gender,
   * joining_date, suggested permanent_address) are server-authoritative —
   * the create POST only accepts `roles`, `permanent_address`, and
   * `district`. See User-Onboarding.md §4.1.
   */
  async getUserCreationPrefill(caseId: number | string): Promise<UserCreationPrefill> {
    const url = `/console/user_onboarding/create/user/${encodeURIComponent(String(caseId))}`;
    try {
      const response = await apiClient.get<{ data: UserCreationPrefill }>(url);
      return response.data.data;
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        (data && (data.message || data.error)) ||
        error?.message ||
        'Could not load user creation details for this candidate.';
      const wrapped = new Error(message);
      (wrapped as any).status = error?.response?.status;
      (wrapped as any).serverError = data;
      throw wrapped;
    }
  }

  /**
   * Create the MIS / FusionAuth user from an onboarded candidate. Body
   * contains only the fields the console user is allowed to set — every
   * other field is taken from the case (identity, employee id, bank KYC,
   * vendor code, etc.) so the request stays small and tamper-resistant.
   *
   * Returns whatever the server replies — typically `{message, data}`
   * where `data` may include a one-time `password` for new users. Callers
   * MUST treat the password as sensitive and never log it.
   */
  async createUserFromCase(
    caseId: number | string,
    body: CreateUserFromCaseRequest,
  ): Promise<CreateUserFromCaseResponse> {
    const url = `/console/user_onboarding/create/user/${encodeURIComponent(String(caseId))}`;
    try {
      const response = await apiClient.post<CreateUserFromCaseResponse>(url, body);
      return response.data;
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        (data && (data.message || data.error)) ||
        error?.message ||
        'Could not create a user from this candidate.';
      const wrapped = new Error(message);
      (wrapped as any).status = error?.response?.status;
      (wrapped as any).serverError = data;
      throw wrapped;
    }
  }

  /**
   * Push one or more candidate cases to SharePoint.
   * `POST /console/user_onboarding/sharepoint/sync` with `{ case_ids: string[] }`.
   */
  async syncToSharepoint(caseIds: Array<number | string>): Promise<{ message?: string }> {
    try {
      const response = await apiClient.post<{ message?: string }>(
        this.endpoints.sharepointSync,
        { case_ids: caseIds.map((caseId) => String(caseId)) },
        { timeout: 120000 },
      );
      return response.data ?? {};
    } catch (error: any) {
      const data = error?.response?.data;
      const message =
        (data && (data.message || data.error)) ||
        error?.message ||
        'Could not sync this candidate to SharePoint.';
      const wrapped = new Error(message);
      (wrapped as any).status = error?.response?.status;
      (wrapped as any).serverError = data;
      throw wrapped;
    }
  }
}

/** Server-side prefill returned by `GET /console/user_onboarding/create/user/:case_id`. */
export interface UserCreationPrefill {
  db_id: number | null;
  fusion_auth_user_id: string | null;
  employee_id: string;
  email: string;
  first_name: string;
  middle_name?: string | null;
  last_name?: string | null;
  gender: string;
  joining_date: string;
  permanent_address: string;
  phone_number: string;
}

/** Console-user-supplied fields for `POST /console/user_onboarding/create/user/:case_id`. */
export interface CreateUserFromCaseRequest {
  /** Required for new-user creation. Empty array on reactivation paths is rejected. */
  roles: string[];
  /** Required. Free text; the GET suggested value can be edited before submit. */
  permanent_address: string;
  /** Optional. Empty string allowed. Comma-separated list, `*`, or known district names. */
  district?: string;
}

/**
 * Village-lookup payload from
 * `GET /console/user_onboarding/invite/location/village/:lgd_code`.
 * Keys are the English-locale `*_in_english` columns plus pincode.
 */
export interface VillageLookupDetails {
  location_name_in_english?: string;
  subdistrict_name_in_english?: string;
  district_name_in_english?: string;
  state_name_in_english?: string;
  pincode?: string;
}

/** Loose response shape — the server may return identifiers and/or a one-time password. */
export interface CreateUserFromCaseResponse {
  message?: string;
  data?: {
    db_id?: number;
    fusion_auth_user_id?: string;
    employee_id?: string;
    email?: string;
    /** One-time password for newly created users. Sensitive — never log. */
    password?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export const onboardingService = new OnboardingService();
export default onboardingService;
